import { useCallback, useMemo } from 'react'
import { useAppSelector } from 'redux/toolkit/hooks'
import { UserRole } from 'config/userRole'
import useUserRights from 'components/libs/userRights/useUserRights'
import { DomainType } from 'types/domains'
import { useIsAccountHelpdeskUser } from 'lib/hooks/useIsAccountHelpdeskUser'

export const useDomainsTabRights = () => {
  const { userHasRight, isUserType } = useUserRights()
  const isCplAccount = useAppSelector(_store => !!_store.auth.accessTokenObject?.isCplAccount)
  const isAccountHelpdeskUser = useIsAccountHelpdeskUser()

  const canAccessDomainsTab = useMemo(
    () => userHasRight(UserRole.DOMAIN_USER) || userHasRight(UserRole.HELPDESK_ROLE),
    [userHasRight]
  )

  const canAddDomain = useMemo(
    () => userHasRight(UserRole.ACCOUNT_USER) || isAccountHelpdeskUser,
    [userHasRight, isAccountHelpdeskUser]
  )

  const canDeleteDomain = useMemo(() => userHasRight(UserRole.ACCOUNT_USER), [userHasRight])

  const canSeeOutboundSmarthost = useMemo(() => !isCplAccount, [isCplAccount])

  const canSeeEmailContinuityStatus = useMemo(() => !isCplAccount, [isCplAccount])

  const shouldSeeHybridLabel = useCallback(
    (domainType: string) => domainType === DomainType.HYBRID && !isCplAccount,
    [isCplAccount]
  )

  const canEditDomain = useMemo(() => !isUserType(UserRole.HELPDESK_ROLE), [isUserType])

  const canManageAlias = useMemo(() => !isCplAccount, [isCplAccount])

  const canManageAdvancedSmtpEncryptionSettings = useMemo(() => !isCplAccount, [isCplAccount])

  const canManageEncryption = useMemo(() => !isCplAccount, [isCplAccount])

  const canManageUserAutoAdd = useMemo(() => !isCplAccount, [isCplAccount])

  const canManageRecipientRewrite = useMemo(() => !isCplAccount, [isCplAccount])

  const canManageDirectoryServices = useMemo(() => !isCplAccount, [isCplAccount])

  const canManageAdvancedConfiguration = useMemo(() => !isCplAccount, [isCplAccount])

  return useMemo(
    () => ({
      canAccessDomainsTab,
      canAddDomain,
      canDeleteDomain,
      canSeeOutboundSmarthost,
      canSeeEmailContinuityStatus,
      shouldSeeHybridLabel,
      canEditDomain,
      canManageAlias,
      canManageAdvancedSmtpEncryptionSettings,
      canManageEncryption,
      canManageUserAutoAdd,
      canManageRecipientRewrite,
      canManageDirectoryServices,
      canManageAdvancedConfiguration
    }),
    [
      canAccessDomainsTab,
      canAddDomain,
      canDeleteDomain,
      canSeeOutboundSmarthost,
      canSeeEmailContinuityStatus,
      shouldSeeHybridLabel,
      canEditDomain,
      canManageAlias,
      canManageAdvancedSmtpEncryptionSettings,
      canManageEncryption,
      canManageUserAutoAdd,
      canManageRecipientRewrite,
      canManageDirectoryServices,
      canManageAdvancedConfiguration
    ]
  )
}

export type DomainsTabRights = ReturnType<typeof useDomainsTabRights>
